<template>
  <div>
    <ListWrapper
      ref="listWrapper"
      :url="url"
      :title="isArchivePage ? $lang.parvandeh_archived : ''"
      :extraParams="extraParams"
      :extraButtons="extraButtons"
      :addButtonText="$lang.new_parvandeh"
      :emptyImage="generateEmptyImage"
      :emptyText="generateEmptyText"
      :hasBackButton="isArchivePage"
      backButtonIcon="arrow-right"
      :hasAddButton="!isArchivePage"
      @handleBackButton="goToParvandehPage"
      @getPermissions="getPermissions"
      @getOtherData="updateOtherData"
      @handleAddButton="goToAddParvandeh"
    >
      <template slot-scope="{ items }">
        <CardList
          :items="items"
          loadingComponent="Parvandeh"
          :showBody="showBody"
        >
          <template slot="header" slot-scope="{ item }">
            <ParvandehHeaderItem
              :item="item"
              :permissions="permissions"
              :itemButtons="itemButtons"
            />
          </template>
          <template slot="body">
            <b-row class="m-0" v-if="bodyData">
              <b-col cols="12" class="padding-0 margin-bottom-8">
                {{ $lang.national_code }}:
                <CopyTextWrapper
                  :id="bodyData.nationalCode"
                  :text="bodyData.nationalCode"
                  textClass="cursor--pointer"
                />
              </b-col>
              <b-col cols="12" class="padding-0 margin-bottom-8">
                {{ $lang.mobile }}:
                <CopyTextWrapper
                  :id="bodyData.mobile"
                  :text="bodyData.mobile"
                  textClass="cursor--pointer"
                />
              </b-col>
              <b-col
                v-if="!!bodyData.statusEndReason"
                cols="12"
                class="padding-0 margin-bottom-8"
              >
                {{ $lang.status_end_reason }}: {{ bodyData.statusEndReason }}
              </b-col>
              <b-col
                v-if="!!bodyData.cancelDescription"
                cols="12"
                class="padding-0 margin-bottom-8"
              >
                {{ $lang.cancel_description }}: {{ bodyData.cancelDescription }}
              </b-col>
              <b-col
                v-if="!!bodyData.cancelDate"
                cols="12"
                class="padding-0 margin-bottom-8"
              >
                {{ $lang.cancel_date }}: <span v-html="bodyData.cancelDate" />
              </b-col>
              <b-col
                v-if="bodyData.roles.length"
                cols="12"
                class="padding-0 margin-bottom-8 flex-start"
              >
                {{ $lang.roles }}:
                {{ bodyData.roles.map((role) => role.caption).join(" | ") }}
              </b-col>
              <b-col
                v-if="bodyData.tags.length"
                cols="12"
                class="padding-0 margin-bottom-8 flex-start"
              >
                {{ $lang.tags }}:
                <div class="flex-start margin-right-4">
                  <span
                    class="chips chips--light-gray margin-right-4"
                    v-for="(tag, index) in bodyData.tags"
                    :key="`a-${index}`"
                  >
                    {{ tag.caption }}
                  </span>
                </div>
              </b-col>
              <b-col
                v-if="!!bodyData.description"
                cols="12"
                class="padding-0 margin-bottom-8"
              >
                {{ $lang.description }}: {{ bodyData.description }}
              </b-col>
              <b-col cols="12" class="padding-0 margin-bottom-8">
                <span class="margin-left-16">
                  {{ $lang.created_by }}: {{ bodyData.createdBy }}
                </span>
                <span>
                  {{ $lang.created_at }}:
                  <span v-html="bodyData.createdAt" />
                </span>
              </b-col>
            </b-row>
          </template>
        </CardList>
      </template>
    </ListWrapper>
    <Modal
      :show="personInfoModal"
      :closeOnBackdrop="false"
      :hasClose="true"
      :hasFooter="personInformationHasFooter"
      :okButton="true"
      :cancelButton="false"
      :okButtonText="$lang.get_pdf_export"
      okButtonVariant="primary"
      size="md"
      bodyClass="overflow-y-auto"
      :ok="print"
      @close="closePersonInfoModal"
    >
      <template #body>
        <PersonInformation
          :code="personInfoCode"
          @trackLoading="toggleFooter"
          @close="closePersonInfoModal"
        />
      </template>
    </Modal>
    <Modal
      :show="refreshParvandehStatusConfirmModal"
      :title="$lang.profileLang.refresh_parvandeh_status"
      :closeOnBackdrop="false"
      :hasClose="false"
      :hasFooter="true"
      :okButton="true"
      :okButtonText="$lang.updating"
      size="sm"
      :ok="refreshAllParvandehStatus"
      @close="closeRefreshParvandehStatusConfirmModal"
    >
      <template #body>
        <div
          class="color-complementary-300 line-height-24"
          v-html="refreshParvandehStatusConfirmDescription"
        ></div>
      </template>
    </Modal>
    <Modal
      :show="uploadReportModal"
      :title="`${$lang.upload_report_for} ${parvandehSelectedData.fullName}`"
      :closeOnBackdrop="false"
      :hasClose="false"
      :hasFooter="false"
      size="sm"
      @close="closeUploadReportModal"
    >
      <template #body>
        <ParvandehReportsUploader
          resourceName="parvandeh"
          :resourceValue="parvandehSelectedData.code"
          :parvandehCode="parvandehSelectedData.code"
          @handleClose="closeUploadReportModal"
        />
      </template>
    </Modal>
    <EditUserPin
      :visible="!!toggleEditUserPin"
      :code="parvandehSelectedData.userCode"
      :canChangeUserPass="canChangeUserPass"
      @close="closeEditUserPin"
    />
    <ParvandehStatusPin
      :visible="toggleParvandehStatusPin"
      :code="parvandehSelectedData.code"
      :title="parvandehSelectedData.parvandehStatusPinTitle"
      @close="closeParvandehStatusPin"
    />
    <ParvandehReportsListPin
      :visible="toggleReportsListPin"
      :code="parvandehSelectedData.code"
      :title="parvandehSelectedData.parvandehReportsListPinTitle"
      :saltFileName="parvandehSelectedData.parvandehNumber"
      @close="closeReportsListPin"
    />
    <ParvandehRolesPin
      :visible="toggleParvandehRolesPin"
      :parvandehData="parvandehSelectedData"
      :title="parvandehSelectedData.parvandehRolePinTitle"
      @close="closeParvandehRolesPin"
    />
    <Modal
      :show="parvandehToArchiveModal"
      :title="parvandehSelectedData.parvandehToArchiveModalTitle"
      :closeOnBackdrop="false"
      :hasClose="false"
      :hasFooter="true"
      :okButton="true"
      :okButtonText="$lang.submitButton"
      size="sm"
      :ok="submitParvandehToArchive"
      @close="closeParvandehToArchiveModal"
    >
      <template #body>
        <b-row class="margin-0">
          <b-col
            cols="12"
            class="color-complementary-300 line-height-24 padding-0 margin-bottom-16"
          >
            {{ $lang.parvandeh_archive_reason_message }}
          </b-col>
          <b-col cols="12" class="padding-0">
            <EnumDropdown
              :title="$lang.parvandeh_archive_reason"
              :label="$lang.parvandeh_archive_reason"
              :placeholder="$lang.parvandeh_archive_reason_select"
              :value="form.parvandehArchiveReason"
              name="parvandehArchiveReason"
              id="parvandehArchiveReason"
              categoryName="parvandeh_end_reason"
              :multiple="false"
              :vuelidateObj="$v.form.parvandehArchiveReason"
              @input="input"
              @focus="resetValidation"
              @blur="checkValidation"
            />
          </b-col>
        </b-row>
      </template>
    </Modal>
    <Modal
      :show="archiveToParvandehModal"
      :title="$lang.cancel_archive"
      :closeOnBackdrop="false"
      :hasClose="false"
      :hasFooter="true"
      :okButton="true"
      :okButtonText="$lang.submitButton"
      size="sm"
      :ok="submitArchiveToParvandeh"
      @close="closeArchiveToParvandehModal"
    >
      <template #body>
        <div
          class="color-complementary-300 line-height-24"
          v-html="archiveToParvandehModalDescription"
        ></div>
      </template>
    </Modal>
    <Modal
      :show="isUsersGroupUploadModal"
      :title="$lang.usersGroupUpload"
      :closeOnBackdrop="false"
      :hasClose="false"
      :hasFooter="false"
      size="sm"
    >
      <template #body>
        <ResourceUploader
          :url="usersGroupUploadUrl"
          :label="$lang.attachFile"
          :hasEditTitle="true"
          :cancelButton="true"
          exampleFileName="import_term_parvandeh_data"
          uploaderName="parvandeh_import"
          @onSuccessUpload="handleSuccessUsersGroupUpload"
          @close="handleCloseUsersGroupUploadModal"
        />
      </template>
    </Modal>
  </div>
</template>

<script>
import inputMixin from "@/mixin/inputMixin.vue";
import {
  ListWrapper,
  CardList,
  Modal,
  PersonInformation,
  ParvandehReportsListPin,
  ParvandehReportsUploader,
  ResourceUploader,
  CopyTextWrapper,
} from "@/components/re-design";
import { EnumDropdown } from "@/components/re-design/forms";
import ParvandehHeaderItem from "./components/ParvandehHeaderItem.vue";
import EditUserPin from "./components/edit-user/EditUserPin.vue";
import ParvandehStatusPin from "./components/ParvandehStatusPin.vue";
import ParvandehRolesPin from "./components/parvandeh-role/ParvandehRolesPin.vue";
import { required } from "vuelidate/lib/validators";
import { pathOr } from "ramda";
import { generateApiUrl, goToRoute } from "@/shared/utils";
import {
  moveParvandehToArchive,
  getOneParvandeh,
  refreshParvandehStatus,
  moveArchiveToParvandeh,
} from "@/modules/profile/services";
import { mapActions, mapGetters } from "vuex";
import { getReportSettingCombo } from "@/services";

export default {
  mixins: [inputMixin],
  components: {
    ListWrapper,
    CardList,
    ParvandehHeaderItem,
    Modal,
    PersonInformation,
    EnumDropdown,
    EditUserPin,
    ParvandehStatusPin,
    ParvandehReportsListPin,
    ParvandehReportsUploader,
    ResourceUploader,
    CopyTextWrapper,
    ParvandehRolesPin,
  },
  validations() {
    return {
      form: {
        reportSettingSelected: { required },
        parvandehArchiveReason: { required },
      },
    };
  },
  data() {
    return {
      parvandehNumberParam: this.$route.params.parvandeh_number,
      bodyDataCached: [],
      bodyData: null,
      refreshParvandehStatusConfirmModal: false,
      refreshParvandehStatusLoading: false,
      permissions: [],
      itemMenuId: 0,
      profilePopupId: null,
      parvandehSelected: null,
      toggleEditUserPin: false,
      personInfoCode: null,
      personInfoModal: false,
      personInformationHasFooter: true,
      uploadReportModal: false,
      form: {
        reportSettingSelected: null,
        parvandehArchiveReason: null,
      },
      reportSettingOptions: [],
      reportSettingLoading: false,
      toggleParvandehStatusPin: false,
      toggleReportsListPin: false,
      toggleParvandehRolesPin: false,
      isArchivePage: false,
      parvandehToArchiveModal: false,
      archiveToParvandehModal: false,
      isUsersGroupUploadModal: false,
      otherData: null,
    };
  },
  computed: {
    ...mapGetters(["userInfo"]),
    url() {
      return this.isArchivePage
        ? generateApiUrl(["profile", "v2", "parvandeh_archive"])
        : generateApiUrl(["profile", "v2", "parvandeh"]);
    },
    canChangeUserPass() {
      return pathOr(false, ["can_change_user_pass"], this.otherData);
    },
    extraParams() {
      const data = new Map();

      if (this.parvandehNumberParam) {
        const params = data.get("params");
        data.set("params", {
          ...params,
          search_txt_parvandeh_number: this.parvandehNumberParam,
        });
      }

      return data.get("params");
    },
    extraButtons() {
      return [
        {
          caption: this.$lang.refresh_parvandeh_status,
          icon: "folder-sync-outline",
          show: true,
          permissions: ["admin", "edit"],
          loading: this.refreshParvandehStatusLoading,
          click: this.showRefreshParvandehStatusConfirmModal,
        },
        {
          caption: this.$lang.parvandeh_archive_show,
          icon: "archive-eye-outline",
          show: !this.isArchivePage,
          permissions: ["admin", "edit", "view"],
          loading: false,
          click: this.goToArchivePage,
        },
        {
          caption: this.$lang.parvandeh_show,
          icon: "folder-outline",
          show: this.isArchivePage,
          permissions: ["admin", "edit", "view"],
          loading: false,
          click: this.goToParvandehPage,
        },
        {
          caption: this.$lang.usersGroupUpload,
          icon: "tray-arrow-up",
          show: true,
          permissions: ["admin", "edit"],
          loading: false,
          click: this.handleOpenUsersGroupUploadModal,
        },
      ];
    },
    refreshParvandehStatusConfirmDescription() {
      return `فرایند بروزرسانی داده‌های وضعیت تکمیل پرونده زمانبر است این برزورسانی فقط برای پروند‌های جدید اجرا میشود و داده‌های آن بعد از چند دقیقه بستگی به تعداد پرونده‌های جدید در دسترس خواهد بود .
      <br><br><b>آیا از اجرای فرایند اطمینان دارید ؟</b>`;
    },
    itemButtons() {
      const canChangeUserInfo = pathOr(
        false,
        ["can_change_user_info"],
        this.otherData
      );
      const canUploadReport = pathOr(
        false,
        ["can_upload_report"],
        this.otherData
      );
      return [
        {
          caption: this.$lang.reports,
          icon: "chart-box-outline",
          show: true,
          permissions: ["admin", "edit"],
          loading: false,
          click: this.showReportsListPin,
        },
        {
          caption: this.$lang.show_profile_summary,
          icon: "account-circle-outline",
          show: true,
          permissions: ["admin", "edit"],
          loading: false,
          click: this.showPersonInfoModal,
        },
        {
          caption: this.$lang.user_info,
          icon: "account-edit-outline",
          show: !this.isArchivePage && canChangeUserInfo,
          permissions: ["admin", "edit"],
          loading: false,
          click: this.showEditUserPin,
        },
        {
          caption: this.$lang.edit_role,
          icon: "account-cog-outline",
          show: !this.isArchivePage,
          permissions: ["admin", "edit"],
          loading: false,
          click: this.showParvandehRolesPin,
        },
        {
          caption: this.$lang.case_status,
          icon: "folder-information-outline",
          show: !this.isArchivePage,
          permissions: ["admin", "edit"],
          loading: false,
          click: this.showParvandehStatusPin,
        },
        {
          caption: this.$lang.show_in_dashboard,
          icon: "chart-box-outline",
          show: !this.isArchivePage,
          permissions: ["admin"],
          loading: false,
          click: this.goToReportDashboard,
        },
        {
          caption: this.$lang.upload_ready_report,
          icon: "file-upload-outline",
          show: !this.isArchivePage && canUploadReport,
          permissions: ["admin", "edit"],
          loading: false,
          click: this.showUploadReportModal,
        },
        {
          caption: this.$lang.archive,
          icon: "archive-outline",
          show: !this.isArchivePage,
          permissions: ["admin", "edit"],
          loading: false,
          click: this.showParvandehToArchive,
        },
        {
          caption: this.$lang.cancel_archive,
          icon: "archive-arrow-up-outline",
          show: this.isArchivePage,
          permissions: ["admin", "edit"],
          loading: false,
          click: this.showArchiveToParvandehModal,
        },
        {
          caption: this.$lang.more_options,
          icon: "plus",
          show: true,
          permissions: ["admin", "edit"],
          loading: false,
          click: this.goToEditParvandeh,
        },
      ];
    },
    parvandehSelectedData() {
      const code = pathOr("", ["code"], this.parvandehSelected);
      const userCode = pathOr(
        "",
        ["person", "user_code"],
        this.parvandehSelected
      );
      const personCode = pathOr("", ["person", "code"], this.parvandehSelected);
      const avatar = pathOr("", ["image_address"], this.parvandehSelected);
      const parvandehNumber = pathOr(
        "",
        ["parvandeh_number"],
        this.parvandehSelected
      );
      const termCaption = pathOr(
        "",
        ["term", "caption"],
        this.parvandehSelected
      );
      const name = pathOr("", ["person", "name"], this.parvandehSelected);
      const family = pathOr("", ["person", "family"], this.parvandehSelected);
      const fullName = `${name} ${family}`;
      const nationalCode = pathOr(
        "",
        ["person", "national_code"],
        this.parvandehSelected
      );
      const mobile = pathOr("", ["person", "mobile"], this.parvandehSelected);
      const parvandehStatusPinTitle = `${this.$lang.case_status} ${fullName}`;
      const parvandehReportsListPinTitle = `${this.$lang.reports_for} ${fullName}`;
      const parvandehRolePinTitle = `${this.$lang.edit_role_for} ${fullName}`;
      const parvandehToArchiveModalTitle = `${this.$lang.set_parvandeh_archive} ${fullName}`;
      return {
        code,
        userCode,
        personCode,
        avatar,
        fullName,
        nationalCode,
        mobile,
        parvandehNumber,
        termCaption,
        parvandehStatusPinTitle,
        parvandehReportsListPinTitle,
        parvandehRolePinTitle,
        parvandehToArchiveModalTitle,
      };
    },
    resourceUploadUrl() {
      const code = pathOr(null, ["code"], this.parvandehSelected);
      return generateApiUrl(
        ["globalServices", "v2", "uploadResourceReport"],
        ["parvandeh", code]
      );
    },
    uploadReportExtraPayload() {
      return {
        parvandeh_code: pathOr(null, ["code"], this.parvandehSelected),
        report_setting_id: pathOr(
          null,
          ["id"],
          this.form.reportSettingSelected
        ),
      };
    },
    archiveToParvandehModalDescription() {
      return `با تایید این فرایند این پرونده از قسمت بایگانی خارج خواهد شد.
      <br><br><b>آیا از اجرای فرایند اطمینان دارید ؟</b>`;
    },
    generateEmptyImage() {
      return this.isArchivePage
        ? this.$config.images.shared.redesign.empty.archive
        : this.$config.images.shared.redesign.empty.list;
    },
    generateEmptyText() {
      return this.isArchivePage
        ? this.$lang.empty_archive
        : this.$lang.list_empty_text;
    },
    usersGroupUploadUrl() {
      return generateApiUrl(["profile", "v2", "importManualRegister"]);
    },
  },
  methods: {
    ...mapActions(["setAction"]),
    pathOr,
    generateApiUrl,
    goToRoute,
    listWrapperReload() {
      this.$refs.listWrapper.reload();
    },
    async showBody(item) {
      try {
        const code = pathOr(null, ["code"], item);
        // check exist in cache data
        const cacheData = this.bodyDataCached.find(
          (cacheItem) => cacheItem.code === code
        );
        if (cacheData) {
          this.bodyData = cacheData;
          return;
        }

        const response = await getOneParvandeh(code);
        this.bodyData = response;
        this.bodyDataCached = [...this.bodyDataCached, response];
      } catch (error) {
        this.$generateError(error);
      }
    },
    showRefreshParvandehStatusConfirmModal() {
      this.refreshParvandehStatusConfirmModal = true;
    },
    closeRefreshParvandehStatusConfirmModal() {
      this.refreshParvandehStatusConfirmModal = false;
    },
    async refreshAllParvandehStatus() {
      try {
        this.refreshParvandehStatusLoading = true;
        const response = await refreshParvandehStatus();
        const { message } = response;
        this.$notify.success(message);
        this.closeRefreshParvandehStatusConfirmModal();
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.refreshParvandehStatusLoading = false;
      }
    },
    getPermissions(value) {
      this.permissions = value;
    },
    updateOtherData(data) {
      this.otherData = data;
    },
    showEditUserPin(item) {
      this.parvandehSelected = item;
      this.toggleEditUserPin = true;
    },
    closeEditUserPin() {
      this.toggleEditUserPin = false;
    },
    goToAddParvandeh() {
      this.setAction({
        key: "mobileHeaderTitleText",
        value: null,
      });
      this.setAction({
        key: "mobileHeaderBaseRoute",
        value: this.$route.name,
      });
      goToRoute("add-parvandeh", {}, {}, false);
    },
    setMobileHeaderData(item) {
      const name = pathOr("", ["person", "name"], item);
      const family = pathOr("", ["person", "family"], item);
      const fullName = `${name} ${family}`;
      const title = `${this.$lang.more_options_in_parvandeh} ${fullName}`;
      this.setAction({
        key: "mobileHeaderTitleText",
        value: title,
      });
      this.setAction({
        key: "mobileHeaderBaseRoute",
        value: this.$route.name,
      });
    },
    goToEditParvandeh(item) {
      this.setMobileHeaderData(item);
      const parvandehCode = pathOr(null, ["code"], item);
      const params = { code: parvandehCode };
      goToRoute("edit-parvandeh", params, {}, false);
    },
    showPersonInfoModal(item) {
      this.personInfoCode = pathOr(null, ["person", "code"], item);
      this.personInfoModal = true;
    },
    closePersonInfoModal() {
      this.personInfoCode = null;
      this.personInfoModal = false;
    },
    print() {
      window.print();
    },
    toggleFooter(state) {
      this.personInformationHasFooter = !state;
    },
    showUploadReportModal(item) {
      this.parvandehSelected = item;
      this.uploadReportModal = true;
      this.getReportSettingOptions();
    },
    closeUploadReportModal() {
      this.form.reportSettingSelected = null;
      this.uploadReportModal = false;
    },
    async getReportSettingOptions() {
      try {
        this.reportSettingLoading = true;
        const response = await getReportSettingCombo();
        const { options } = response;
        this.reportSettingOptions = options;
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.reportSettingLoading = false;
      }
    },
    showParvandehStatusPin(item) {
      this.parvandehSelected = item;
      this.toggleParvandehStatusPin = true;
    },
    closeParvandehStatusPin() {
      this.toggleParvandehStatusPin = false;
    },
    showReportsListPin(item) {
      this.parvandehSelected = item;
      this.toggleReportsListPin = true;
    },
    closeReportsListPin() {
      this.toggleReportsListPin = false;
    },
    showParvandehRolesPin(item) {
      this.parvandehSelected = item;
      this.toggleParvandehRolesPin = true;
    },
    closeParvandehRolesPin() {
      this.toggleParvandehRolesPin = false;
    },
    showParvandehToArchive(item) {
      this.parvandehSelected = item;
      this.parvandehToArchiveModal = true;
    },
    closeParvandehToArchiveModal() {
      this.parvandehToArchiveModal = false;
    },
    async submitParvandehToArchive() {
      this.$v.$touch();
      if (this.$v.form.parvandehArchiveReason.$anyError) return;

      try {
        const { code } = this.parvandehSelectedData;
        const reasonId = pathOr(null, ["id"], this.form.parvandehArchiveReason);
        const response = await moveParvandehToArchive(code, reasonId);
        const { message } = response;
        this.$notify.success(message);
        this.listWrapperReload();
        this.form.parvandehArchiveReason = null;
        this.closeParvandehToArchiveModal();
      } catch (error) {
        this.$generateError(error);
      }
    },
    showArchiveToParvandehModal(item) {
      this.parvandehSelected = item;
      this.archiveToParvandehModal = true;
    },
    closeArchiveToParvandehModal() {
      this.archiveToParvandehModal = false;
    },
    async submitArchiveToParvandeh() {
      try {
        const { code } = this.parvandehSelectedData;
        const response = await moveArchiveToParvandeh(code);
        const { message } = response;
        this.$notify.success(message);
        this.listWrapperReload();
        this.closeArchiveToParvandehModal();
      } catch (error) {
        this.$generateError(error);
      }
    },
    goToArchivePage() {
      this.isArchivePage = true;
    },
    goToParvandehPage() {
      this.isArchivePage = false;
    },
    goToReportDashboard(item) {
      const image = pathOr("", ["image_address"], item);
      const name = pathOr("", ["person", "name"], item);
      const family = pathOr("", ["person", "family"], item);
      const data = [
        {
          code: pathOr(null, ["code"], item),
          parvandehNumber: pathOr(null, ["parvandeh_number"], item),
          fullName: `${name} ${family}`,
          image,
          termCaption: pathOr("", ["term", "caption"], item),
        },
      ];
      localStorage.setItem("dashboard-parvandeh-codes", JSON.stringify(data));
      goToRoute("report_dashboard", {}, {}, false);
    },
    handleOpenUsersGroupUploadModal() {
      this.isUsersGroupUploadModal = true;
    },
    handleCloseUsersGroupUploadModal() {
      this.isUsersGroupUploadModal = false;
    },
    handleSuccessUsersGroupUpload() {
      this.handleCloseUsersGroupUploadModal();
      this.listWrapperReload();
    },
  },
};
</script>
